<template>
  <ion-page>
    <ion-header>
      <toolbar
        @reRenderToolbar="refetchCartItems()"
        @clearCart="clearCart"
        :selectedCompany="selectedCompany"
        :isOnScrollTop="isOnScrollTop"
        :showClearButton="!isCartEmpty"
      />
    </ion-header>

    <ion-content ref="contentRef" :scroll-events="true" @ionScroll="handleScroll($event)">
      <ion-refresher slot="fixed" @ionRefresh="handleRefresh($event)">
        <ion-refresher-content></ion-refresher-content>
      </ion-refresher>
      <PullRefresh v-if="!isOnline"></PullRefresh>
      <div v-if="isOnline">
        <div v-if="isCartEmpty" class="cart-empty">
          <ion-img class="cart-empty-img" :src="imageDefault"> </ion-img>
          <div class="d-flex justify-center flex-column mt-2" align="center">
            <div>
              <ion-label color="grey" class="fs-3 fw-bold">{{ $t('sorry') }}</ion-label>
            </div>
            <div>
              <ion-label color="grey" class="msg-empty">{{ $t('message_cart_empty') }}</ion-label>
            </div>
          </div>
        </div>
        <div v-else>
          <div v-if="groupedCartByTenant" class="block-tenant-list">
            <ion-grid
              class="pa-3 mt-3 block-tenant-item"
              v-for="(tenantItems, index) in groupedCartByTenant"
              :key="index"
            >
              <!-- checkbox all -->
              <ion-row class="pb-1 ion-align-items-top">
                <ion-col size="1">
                  <div
                    class="item-checkbox"
                    :class="disableCheckboxAll && 'disabled-checkbox'"
                    @click="toggleCheckboxAllByTenant(tenantItems[0].tenant_id)"
                  >
                    <ion-icon
                      color="primary"
                      v-if="isCheckAllByTenant(tenantItems[0].tenant_id) === true"
                      :icon="checkbox"
                    ></ion-icon>
                    <ion-icon
                      color="grey2"
                      v-else-if="isCheckAllByTenant(tenantItems[0].tenant_id) === -1"
                      :icon="square"
                    ></ion-icon>
                    <ion-icon color="grey5" v-else :icon="squareOutline"></ion-icon>
                  </div>
                </ion-col>
                <ion-col size="11" class="d-flex align-self-center fw-500">
                  <div>
                    <ion-row>
                      <ion-label class="tenant_name">{{ tenantItems[0].tenant_name }}</ion-label>
                    </ion-row>
                    <ion-row class="mt-1">
                      <ion-label class="delivery_fee_warn" color="primary">
                        {{ spendToFreeDeliveryMessage[tenantItems[0].tenant_id] }}
                      </ion-label>
                    </ion-row>
                  </div>
                </ion-col>
              </ion-row>

              <!-- list items -->
              <div :key="reRenderCartItems">
                <cart-item
                  v-for="item in tenantItems"
                  :key="item.id"
                  :item="item"
                  :currencySymbol="currencySymbol"
                  @update-quantity-weight="updateQuantityWeightItem"
                  @open-modal-delete="openModalDelete"
                  @toggle-checkbox="toggleCheckboxItem"
                  @uncheck-checkbox="uncheckCheckboxItem"
                />
              </div>
            </ion-grid>
          </div>
        </div>
      </div>
    </ion-content>

    <ion-footer v-if="!isCartEmpty && isOnline">
      <ion-toolbar>
        <ion-grid>
          <ion-row>
            <ion-col size="6">
              <div>
                <ion-label class="fs-3">{{ $t('total_price') }}</ion-label>
              </div>
              <div class="mt-2">
                <ion-label class="fs-4 fw-bold" color="primary">
                  <span>{{ totalPriceSelected }}</span>
                </ion-label>
              </div>
            </ion-col>
            <ion-col size="6" class="d-flex align-center justify-end">
              <ion-button
                @click="orderNow"
                :disabled="totalItemSelected === 0"
                class="text-capitalize"
                color="primary"
              >
                {{ $t('order_now') }} ({{ totalItemSelected }})
              </ion-button>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-toolbar>
    </ion-footer>
    <ion-modal :is-open="isOpenCheckoutRef" css-class="modal-checkout" @didDismiss="setOpenCheckout(false)">
      <modal-checkout
        :cartData="cart"
        :itemsSelected="itemsSelected"
        @close-modal="setOpenCheckout(false)"
        @place-order="placeOrder"
        @refresh-cart="refreshCartItems()"
      ></modal-checkout>
    </ion-modal>
    <ion-modal
      mode="ios"
      backdrop-dismiss="false"
      :is-open="isOpenPriceUpdated"
      css-class="modal-price-updated"
      @didDismiss="setOpenModalPriceUpdate(false)"
    >
      <price-update-alert @close-modal="setOpenModalPriceUpdate(false)"></price-update-alert>
    </ion-modal>
    <ion-modal
      mode="ios"
      backdrop-dismiss="false"
      :is-open="isOpenItemsNoAvailable"
      css-class="modal-price-updated"
      @didDismiss="closeNoAvailableItems"
    >
      <alert-info-modal
        :message="$t('some_item_no_available')"
        :buttonText="$t('return_to_cart')"
        @close-modal="closeNoAvailableItems"
      ></alert-info-modal>
    </ion-modal>
  </ion-page>
</template>
<script>
import { apolloClient } from '@/main';
import { emptyCartImgage, USER_PERMISSION_KEYS } from '@/modules/sale/constants';
import { saleGetCustomerDetail, saleGetLatestPrice } from '@/modules/sale/services/graphql';
import { displayPrice, randomId } from '@/modules/sale/services/libs/helper';
import { ACTIONS as ACTIONS_CART, ACTIONS as ACTIONS_UPDATE_PRICE } from '@/modules/sale/store/cart/actions';
import { presentAlert } from '@/modules/shared/utils';
import { isQuantityAMultipleOfIncrement, useLoading } from '@/modules/shared/utils/';
import { setBadgeNumber } from '@/modules/shared/utils/badge';
import PullRefresh from '@/modules/shared/views/refreshPage/PullRefresh.vue';
import { allUserPermissions, getDeliveryFeeConfig } from '@/services/shared/graphql';
import { comparePriceArrays } from '@/services/shared/helper/helper';
import { useAlert } from '@/usecases/global';
import { getPriceByTaxConfig, getTaxAmount, priceFormatter } from '@/utils/';
import { modalController } from '@ionic/vue';
import { arrowDownOutline, checkbox, chevronForwardOutline, square, squareOutline } from 'ionicons/icons';
import _ from 'lodash';
import debounce from 'lodash.debounce';
import { defineAsyncComponent, defineComponent, ref } from 'vue';
import { createNamespacedHelpers, useStore } from 'vuex';
import PriceUpdateAlert from './components/PriceUpdateAlert.vue';
import Toolbar from './components/Toolbar.vue';

const { mapGetters: mapGettersCart, mapActions: mapActionsCart } = createNamespacedHelpers('sale/cart');
const { mapActions: mapActionsPriceUpdated } = createNamespacedHelpers('sale/cart');
const { mapGetters: mapGettersCustomer } = createNamespacedHelpers('sale/customer');

export default defineComponent({
  name: 'Cart',
  props: {
    timeout: { type: Number, default: 1000 }
  },
  components: {
    Toolbar,
    // components
    CartItem: defineAsyncComponent(() => import('./components/CartItem.vue')),
    ModalCheckout: defineAsyncComponent(() => import('./components/ModalCheckout/Index.vue')),
    AlertInfoModal: defineAsyncComponent(() => import('@/modules/shared/components/AlertInfoModal.vue')),
    PriceUpdateAlert,
    PullRefresh
  },

  setup() {
    const { createAlertTwoAction, createAlert } = useAlert();
    const store = useStore();
    const imageDefault = ref(emptyCartImgage);
    const user = ref(null);
    const selectedCompany = ref(null);
    const allTenantSelected = ref([]);
    const uniqueId = () => randomId();
    const groupedCartByTenant = ref([]);
    const isOpenCheckoutRef = ref(false);
    const setOpenCheckout = (state) => (isOpenCheckoutRef.value = state);
    const deleteItem = ref(null);
    const localCart = ref(null);
    const checkLatestPrice = ref([]);
    const latesCartItemPrice = ref(null);
    const currentCartItemsPrice = ref([]);
    const latestPrice = ref([]);
    const latesCartItem = ref([]);
    const oosItemSelected = ref([]);
    const itemsUpdate = ref({});
    const { showLoading } = useLoading();
    const latestPriceError = ref(false);
    const isOpenItemsNoAvailable = ref(false);
    const isOnScrollTop = ref(false);
    const reRenderCartItems = ref(1);
    const setOpenItemsNoAvailable = (state) => (isOpenItemsNoAvailable.value = state);
    const deliveryFeeConfig = ref({});
    return {
      deliveryFeeConfig,
      reRenderCartItems,
      allTenantSelected,
      showLoading,
      user,
      uniqueId,
      groupedCartByTenant,
      selectedCompany,
      deleteItem,
      isOpenCheckoutRef,
      setOpenCheckout,
      priceFormatter,
      checkLatestPrice,
      latesCartItemPrice,
      currentCartItemsPrice,
      latestPrice,
      oosItemSelected,
      latesCartItem,
      itemsUpdate,
      isOpenItemsNoAvailable,
      setOpenItemsNoAvailable,
      // image
      imageDefault,
      chevronForwardOutline,
      checkbox,
      squareOutline,
      square,
      // controller
      modalController,
      localCart,
      latestPriceError,
      isOnline: ref(true),
      arrowDownOutline,
      store,
      isOnScrollTop,
      createAlertTwoAction,
      createAlert,
      isOpenPriceUpdated: ref(false),
      handleScroll: debounce((e) => {
        const onScrollHeightValue = e.detail.scrollTop;
        if (onScrollHeightValue > 10) isOnScrollTop.value = true;
        if (onScrollHeightValue === 0) isOnScrollTop.value = false;
      }, 200)
    };
  },

  watch: {
    selectedCustomer: {
      async handler(newCompany) {
        await this[ACTIONS_CART.GET_CART_ITEMS]({
          userId: this.user.id,
          customerId: newCompany?.id
        });

        // refresh cart items
        await this.refetchCartItems();
        this.reRenderCartItems++;
      },
      deep: true
    },
    cart(value) {
      this.localCart = value;
      this.getGroupedCartByTenant();
      this.refetchCartItems();
    },
    latesCartItemPrice() {
      this.latestPrice = this.latesCartItemPrice.map(function (cartItem) {
        return {
          price: cartItem.price,
          tax_rate: cartItem.tax_rate
        };
      });
    },
    itemsSelected() {
      const checkOosSelectedItem = this.itemsSelected[0]?.map((item) => item.is_out_of_stock);
      this.oosItemSelected = checkOosSelectedItem?.map((index) => index);
    }
  },

  async ionViewWillEnter() {
    this.user = await this.$storage.getUser();
    this.selectedCompany = await this.$storage.getSelectedCompany();
    this.updateCartToStore();
    this.getDeliveryFeeConfigFn();
  },

  computed: {
    ...mapGettersCustomer(['selectedCustomer']),
    ...mapGettersCart(['cart']),
    currencySymbol() {
      return this.user && this.user.currency_symbol;
    },
    isCartEmpty() {
      return !(this.localCart && this.localCart.items && this.localCart.items.length !== 0);
    },
    isCheckAllByTenant() {
      return (tenantId) => {
        if (!this.allTenantSelected) return false;
        const index = this.allTenantSelected.findIndex((item) => item.tenantId === tenantId);
        if (index === -1) return false;

        if (this.allTenantSelected[index].total === 0) return -1;

        return this.allTenantSelected[index].total === this.allTenantSelected[index].totalSelected;
      };
    },
    totalItemSelected() {
      if (this.isCartEmpty) return 0;

      const items = Object.values(this.groupedCartByTenant).flat();

      return items.reduce((total, current) => {
        if (current.selected && !current.is_out_of_stock && !current.is_unavailable) {
          if (current.is_order_by_weight) {
            total += 1;
          } else {
            total += current.order_qty;
          }
        }
        return total;
      }, 0);
    },
    disableCheckboxAll() {
      const items = Object.values(this.groupedCartByTenant).flat();
      const value = items.reduce((allChecked, current) => {
        if (!this.isItemDisable(current)) {
          allChecked = true;
        }
        return allChecked;
      }, false);
      return !value;
    },
    totalPriceSelected() {
      if (this.isCartEmpty) return 0;
      const totalPrice = this.itemsSelected.flat().reduce((total, item) => total + item.total_price, 0);
      return priceFormatter(this.currencySymbol, displayPrice(totalPrice));
    },
    itemsSelected() {
      const data = Object.values(this.groupedCartByTenant).map((tenantItems) => {
        return tenantItems.filter((item) => item.selected && !item.is_out_of_stock && !item.is_unavailable);
      });
      return data.filter((tenantItems) => tenantItems.length !== 0);
    },
    isAllowToCreateOrderForB2CCustomer() {
      return this.checkPermission(USER_PERMISSION_KEYS.ORDER_CREATE_B2C);
    },
    spendToFreeDeliveryMessage() {
      if (!this.isCartEmpty && this.deliveryFeeConfig.max_order_value > 0) {
        return Object.keys(this.groupedCartByTenant).reduce((acc, tenantId) => {
          const tenantItems = this.groupedCartByTenant[tenantId];
          const orderValue = tenantItems
            .filter((item) => item.selected && !item.is_out_of_stock && !item.is_unavailable)
            .reduce((total, item) => total + item.total_price, 0);
          const remainOrderValue = this.deliveryFeeConfig.max_order_value - orderValue;
          if (remainOrderValue > 0) {
            acc[tenantId] = this.$t('spend_more_to_free_delivery', {
              orderAmount: priceFormatter(this.currencySymbol, remainOrderValue)
            });
          }
          return acc;
        }, {});
      } else {
        return {};
      }
    }
  },

  inject: ['$storage'],

  beforeUpdate() {
    this.getGroupedCartByTenant();
    this.getAllTenantSelected();
    this.handleGetCustomerDetail();
    this.getAllUserPermissions();
  },

  data() {
    return {
      permissions: []
    };
  },

  methods: {
    ...mapActionsCart([
      ACTIONS_CART.GET_CART_ITEMS,
      ACTIONS_CART.CHANGE_CART_ITEMS,
      ACTIONS_CART.REMOVE_CART_ITEM
    ]),
    ...mapActionsPriceUpdated([ACTIONS_UPDATE_PRICE.SALE_UPDATE_LATEST_PRICE]),

    async handleRefresh(event) {
      await this.detectConnection();
      await this.handleGetCustomerDetail();
      await this.updateCartToStore();
      await this.getGroupedCartByTenant();
      await this.getAllTenantSelected();
      event && event.target.complete();
    },
    detectConnection() {
      const connection = navigator.onLine;
      if (connection) {
        this.isOnline = true;
      } else {
        this.isOnline = false;
      }
    },
    placeOrder() {
      this.setOpenCheckout(false);
    },
    async orderNow() {
      const isAllowedToCreateOrderForB2C = this.isAllowToCreateOrderForB2CCustomer;
      const isCustomerB2C = this.selectedCompany?.is_b2c || false;
      if (isCustomerB2C && !isAllowedToCreateOrderForB2C) {
        await presentAlert({
          mode: 'ios',
          message: this.$t('order_create_b2c_permission_not_authorized'),
          buttons: [
            {
              text: 'Ok'
            }
          ]
        });
        return;
      }
      // set the selected items before the cart items updated
      const prevLocalSelectedItems = this.itemsSelected[0]?.map((item) => item);

      //check if the selected cart item oos status have update and then compare it with lates data update
      this.latesCartItem = [];
      await this[ACTIONS_CART.GET_CART_ITEMS]({
        userId: this.user.id,
        customerId: this.selectedCompany.id
      });

      const localCartItems = this.localCart.items;
      let itemSelectedData = this.itemsSelected[0]?.map((item) => item) || [];

      for (let i = 0; i < localCartItems.length; i++) {
        for (let j = 0; j < itemSelectedData.length; j++) {
          if (localCartItems[i].sku_id === itemSelectedData[j].sku_id) {
            this.latesCartItem.push(localCartItems[i].is_out_of_stock);
          }
        }
      }

      const itemsMatch = this.latesCartItem.every((value, index) => value === this.oosItemSelected[index]);
      // check if any items lo longer available
      let hasUnavailableItems = false;
      let hasBlockedCartItems = false;
      // Create a map of latestCartItems for efficient lookup by ID
      const latestCartItemsMap = new Map(localCartItems.map((item) => [item.id, item]));
      // Iterate through prevLocalSelectedItems and compare is_unavailable
      prevLocalSelectedItems.forEach((prevItem) => {
        const latestItem = latestCartItemsMap.get(prevItem.id);
        const orderItemQuantity = latestItem.is_order_by_weight
          ? latestItem.order_weight
          : latestItem.order_qty; // get order weight or order quantity
        const quantityNotOrderable =
          latestItem.total_available_quantity !== null
            ? orderItemQuantity > latestItem.total_available_quantity
            : false; // null means no quantity restriction
        const blockedCartItems =
          latestItem.is_unavailable || (!latestItem.is_back_order && quantityNotOrderable);

        if (blockedCartItems) {
          this.itemsUpdate = {
            ...latestItem,
            order_weight: latestItem.total_available_quantity
              ? Number(latestItem.total_available_quantity.toFixed(2))
              : latestItem.order_weight,
            order_qty: latestItem.total_available_quantity
              ? Number(latestItem.total_available_quantity)
              : latestItem.order_qty
          };
          hasBlockedCartItems = !latestItem.total_available_quantity;
          return;
        }
        if (!hasUnavailableItems) {
          hasUnavailableItems = latestItem?.is_unavailable || hasBlockedCartItems || !itemSelectedData.length;
        }
      });

      if (itemsMatch) {
        // if selected item oos has no update or same with lates cart items data
        // allow to next process to check lates price update
        await this.getLatestPrice();
        await this.compareCartItemPrice();
        if (this.latestPriceError) {
          return;
        }
        if (hasUnavailableItems || hasBlockedCartItems) {
          // show alert info modal that any items no longer available
          this.setOpenItemsNoAvailable(true);
          this.updateQuantityWeightItem(this.itemsUpdate);
          this.refetchCartItems();
        } else if (!comparePriceArrays(this.currentCartItemsPrice, this.latestPrice)) {
          // show price update info if any price update
          this.setOpenModalPriceUpdate(true);
        } else {
          // go to checkout process if there is no price update
          this.setOpenCheckout(true);
        }
      } else {
        // if selected item oos has update or different with lates cart items data
        // reload cart item data
        this.refetchCartItems();
      }
    },
    updateCartToStore() {
      this.showLoading(async () => {
        return this[ACTIONS_CART.GET_CART_ITEMS]({
          userId: this.user.id,
          customerId: this.selectedCompany.id
        });
      });
    },
    async showAlertError(customErrorMsg) {
      this.createAlert(
        `${this.$t('errorTitle')}`,
        customErrorMsg || `${this.$t('something_went_wrong_please_try_again')}`,
        () => {
          this.handleGetCustomerDetail();
          this.detectConnection();
        },
        `${this.$t('OK')}`
      );
    },
    async handleGetCustomerDetail() {
      this.selectedCompany = await this.$storage.getSelectedCompany();
      try {
        const { data } = await apolloClient.query({
          query: saleGetCustomerDetail,
          variables: {
            id: this.selectedCompany.id
          }
        });
        await this.$storage.setSelectedCompany({
          ...this.selectedCompany,
          account_number: data.saleGetCustomerDetail.account_number,
          is_b2c: data.saleGetCustomerDetail.is_b2c
        });
        const updateCompany = await this.$storage.getSelectedCompany();
        this.selectedCompany = updateCompany;
        this.latestPriceError = false;
      } catch (error) {
        console.log(error);
      }
    },
    async openModalDelete(item) {
      await this.createAlertTwoAction(
        `${this.$t('delete_this_item')}`,
        `${this.$t('text_remove_item_in_cart')}`,
        `${this.$t('yes_delete')}`,
        () => this.removeItem(item),
        `${this.$t('cancel')}`,
        `alert-vertical-action`
      );
    },

    async setOpenModalPriceUpdate(params) {
      const cartItemsId = this.localCart.items.map((cartItem) => {
        return cartItem.id;
      });
      let cartItemUpdatePrice = cartItemsId.map((cartItemsId, index_value) => {
        return {
          cartItemId: cartItemsId,
          price: this.latestPrice[index_value].price,
          tax_rate: this.latestPrice[index_value].tax_rate
        };
      });
      if (!params) {
        await this[ACTIONS_UPDATE_PRICE.SALE_UPDATE_LATEST_PRICE]({
          payload: cartItemUpdatePrice
        });
        this.updateCartToStore();
      }
      this.isOpenPriceUpdated = params;
    },

    isItemDisable(item) {
      if (item.is_order_by_weight) {
        return (
          (!item.is_back_order &&
            item.total_available_quantity !== null &&
            item.order_weight > item.total_available_quantity) ||
          item.is_out_of_stock ||
          !item.order_weight ||
          item.is_unavailable
        );
      } else {
        return (
          !isQuantityAMultipleOfIncrement({
            quantity: item.order_qty,
            increment: item.increment_qty
          }) ||
          (!item.is_back_order &&
            item.total_available_quantity !== null &&
            item.order_qty > item.total_available_quantity) ||
          item.is_out_of_stock ||
          !item.order_qty ||
          item.is_unavailable
        );
      }
    },

    async removeItem(item) {
      // find the item in local cart
      this.localCart.items = this.localCart.items.filter((el) => el.id !== item.id);

      await this.showLoading(() => {
        return Promise.allSettled([
          this[ACTIONS_CART.REMOVE_CART_ITEM]({
            id: item.id
          }),
          this.refetchCartItems()
        ]);
      });
    },

    async updateQuantityWeightItem(item) {
      this.localCart.items.forEach((o) => {
        if (o.id === item.id) {
          o.order_qty = !item.is_order_by_weight
            ? item.order_qty
            : item.total_available_quantity > 0 && item.order_weight > item.total_available_quantity
            ? item.total_available_quantity
            : item.order_weight;
          o.order_weight =
            item.total_available_quantity && item.order_weight > item.total_available_quantity
              ? item.total_available_quantity
              : item.order_weight;
          o.selected = item.selected;
        }
      });

      const items = [
        {
          id: item.id,
          orderQuantity: !item.is_order_by_weight
            ? item.order_qty
            : item.total_available_quantity > 0 && item.order_weight > item.total_available_quantity
            ? Number(item.total_available_quantity)
            : Number(item.order_weight),
          orderWeight:
            item.total_available_quantity > 0 && item.order_weight > item.total_available_quantity
              ? item.total_available_quantity
              : item.order_weight,
          selected: item.selected
        }
      ];
      await this.updateItems(items);
    },

    getAllTenantSelected() {
      const data = [];
      const values = Object.values(this.groupedCartByTenant);

      Object.keys(this.groupedCartByTenant).map((tenantId, index) => {
        const totalSelected = values[index].reduce(
          (total, current) => (current.selected ? total + 1 : total),
          0
        );
        const total = values[index].reduce((total, current) => {
          if (current.is_order_by_weight) {
            return current.order_weight > 0 ? total + 1 : total;
          } else if (this.isItemDisable(current)) {
            current.selected = false;
            return total;
          } else {
            return total + 1;
          }
        }, 0);

        data.push({
          tenantId: parseInt(tenantId),
          total,
          totalSelected
        });
      });
      this.allTenantSelected = data;
    },

    getGroupedCartByTenant() {
      if (!this.localCart) return null;

      // disable multiple tenant cart items, only get the current tenant cart items;
      const items = this.localCart.items
        .map((v) => {
          return this.getItemCart(v);
        })
        .filter((item) => item.tenant_id === this.user.tenant.id);

      this.groupedCartByTenant = _.groupBy(items, (item) => item.tenant_id);
    },

    //Check latest items/product price
    async getLatestPrice() {
      if (!this.localCart?.items || this.localCart?.items.length === 0) {
        this.latesCartItemPrice = [];
        return;
      }
      this.checkLatestPrice = this.localCart?.items.map(function (cartItem) {
        return cartItem.sku_id;
      });
      try {
        const { data } = await apolloClient.query({
          query: saleGetLatestPrice,
          variables: {
            skuIds: this.checkLatestPrice,
            accountNumber: this.selectedCompany.account_number
          }
        });
        this.latesCartItemPrice = data.saleGetLatestPrice;
      } catch (error) {
        this.latestPriceError = true;
        if (this.$route.path === '/sale/cart') {
          this.showAlertError(error.message);
        }
      }
    },

    //Compare the current price with the lates price and auto update cart items
    async compareCartItemPrice() {
      if (this.latesCartItemPrice && this.latesCartItemPrice.length) {
        this.currentCartItemsPrice = this.localCart.items.map(function (cartItem) {
          return {
            price: cartItem.price,
            tax_rate: cartItem.tax_rate
          };
        });
        const cartItemsId = this.localCart.items.map(function (cartItem) {
          return cartItem.id;
        });
        let cartItemUpdatePrice = cartItemsId.map((cartItemsId, index_value) => {
          return {
            cartItemId: cartItemsId,
            price: this.latestPrice[index_value].price,
            tax_rate: this.latestPrice[index_value].tax_rate
          };
        });
        if (!comparePriceArrays(this.currentCartItemsPrice, this.latestPrice)) {
          await this[ACTIONS_UPDATE_PRICE.SALE_UPDATE_LATEST_PRICE]({
            payload: cartItemUpdatePrice
          });
          this.updateCartToStore();
        }
      }
    },

    getItemCart(v) {
      const totalPrice = Number(
        displayPrice(
          v.is_order_by_weight
            ? Number((v.price / v.weight) * Number(v.order_weight))
            : v.order_qty * Number(v.price)
        )
      );
      const subtotal = getPriceByTaxConfig(totalPrice, v.price_is_tax_inclusive, v.tax_rate, 'exclusive');
      const tax = v.price_is_tax_inclusive ? totalPrice - subtotal : getTaxAmount(subtotal, v.tax_rate);
      let selected = v.selected;
      if (selected) {
        if (v.is_order_by_weight) {
          selected = v.order_weight > 0;
        } else if (v.max_quantity !== -1) {
          selected =
            v.order_qty <= v.max_quantity ||
            !v.is_out_of_stock ||
            v.total_available_quantity > 0 ||
            !v.is_order_by_weight;
        }
      }
      return {
        ...v,
        quantity: v.order_qty,
        total_price: v.is_sample ? 0 : totalPrice,
        total_price_tax_exclusive: v.is_sample ? 0 : subtotal,
        price: v.is_sample ? 0 : v.price,
        tax: v.is_sample ? 0 : tax,
        available: true,
        selected
      };
    },

    async uncheckCheckboxItem(item) {
      this.localCart.items.forEach((o) => {
        if (o.id === item.id) {
          o.order_qty = !item.is_order_by_weight
            ? item.order_qty
            : item.total_available_quantity > 0 && item.order_weight > item.total_available_quantity
            ? item.total_available_quantity
            : item.order_weight;
          o.order_weight =
            item.total_available_quantity > 0 && item.order_weight > item.total_available_quantity
              ? item.total_available_quantity
              : item.order_weight;
          o.selected = !item.selected;
        }
      });
      const items = [
        {
          id: item.id,
          orderQuantity: !item.is_order_by_weight
            ? item.order_qty
            : item.total_available_quantity > 0 && item.order_weight > item.total_available_quantity
            ? Number(item.total_available_quantity)
            : Number(item.order_weight),
          orderWeight:
            item.total_available_quantity > 0 && item.order_weight > item.total_available_quantity
              ? item.total_available_quantity
              : item.order_weight,
          selected: !item.selected
        }
      ];
      await this.updateItems(items);
    },

    // toggle checkbox item
    async toggleCheckboxItem(item) {
      this.localCart.items.forEach((o) => {
        if (o.id === item.id) {
          o.order_qty = !item.is_order_by_weight
            ? item.order_qty
            : item.total_available_quantity > 0 && item.order_weight > item.total_available_quantity
            ? item.total_available_quantity
            : item.order_weight;
          o.order_weight =
            item.total_available_quantity > 0 && item.order_weight > item.total_available_quantity
              ? item.total_available_quantity
              : item.order_weight;
          o.selected = !item.selected;
        }
      });

      const items = [
        {
          id: item.id,
          orderQuantity: !item.is_order_by_weight
            ? item.order_qty
            : item.total_available_quantity > 0 && item.order_weight > item.total_available_quantity
            ? Number(item.total_available_quantity)
            : Number(item.order_weight),
          orderWeight:
            item.total_available_quantity > 0 && item.order_weight > item.total_available_quantity
              ? item.total_available_quantity
              : item.order_weight,
          selected: !item.selected
        }
      ];
      await this.updateItems(items);
    },

    // toggle checkbox all
    toggleCheckboxAllByTenant(tenantId) {
      if (!this.disableCheckboxAll) {
        const index = this.allTenantSelected.findIndex((item) => item.tenantId === tenantId);
        if (index === -1) return;

        const currentTenant = this.allTenantSelected[index];

        // get all items to update
        const items = this.getListItemsByTenant(
          tenantId,
          currentTenant.totalSelected !== currentTenant.total
        );
        this.updateItems(items);
      }
    },

    async updateItems(items) {
      this.getGroupedCartByTenant();
      this.getAllTenantSelected();
      await this[ACTIONS_CART.CHANGE_CART_ITEMS]({
        customerId: this.selectedCompany.id,
        items
      });
    },

    async refetchCartItems() {
      await this.getLatestPrice();
      await this.getGroupedCartByTenant();
      await this.getAllTenantSelected();
      this.selectedCompany = await this.$storage.getSelectedCompany();
      await setBadgeNumber(this.store, this.selectedCompany.id, this.user.tenant.id);
      await this.compareCartItemPrice();
    },

    closeNoAvailableItems() {
      this.setOpenItemsNoAvailable(false);
      this.updateCartToStore();
    },

    getListItemsByTenant(tenantId, selected) {
      // select/deselect all items in tenant
      this.localCart.items.forEach((item) => {
        const itemInTenant = this.groupedCartByTenant[tenantId].filter((o) => o.id === item.id);
        if (itemInTenant) {
          item.selected = this.isItemDisable(item) ? false : selected;
        }
      });

      const items = this.groupedCartByTenant[tenantId].map((item) => {
        return {
          id: item.id,
          orderQuantity: !item.is_order_by_weight
            ? item.order_qty
            : item.total_available_quantity > 0 && item.order_weight > item.total_available_quantity
            ? parseInt(item.total_available_quantity)
            : parseInt(item.order_weight),
          orderWeight:
            item.total_available_quantity > 0 && item.order_weight > item.total_available_quantity
              ? item.total_available_quantity
              : item.order_weight,
          selected: this.isItemDisable(item) ? false : selected
        };
      });
      return items;
    },

    async refreshCartItems() {
      const prevLocalSelectedItems = this.itemsSelected[0]?.map((item) => item);

      await this[ACTIONS_CART.GET_CART_ITEMS]({
        userId: this.user.id,
        customerId: this.selectedCompany.id
      });

      //check if the selected cart item oos status have update and then compare it with lates data update
      this.latesCartItem = [];
      const localCartItems = this.localCart.items;
      let itemSelectedData = this.itemsSelected[0]?.map((item) => item);

      for (let i = 0; i < localCartItems.length; i++) {
        for (let j = 0; j < itemSelectedData.length; j++) {
          if (localCartItems[i].sku_id === itemSelectedData[j].sku_id) {
            this.latesCartItem.push(localCartItems[i].is_out_of_stock);
          }
        }
      }

      // Create a map of latestCartItems for efficient lookup by ID
      const latestCartItemsMap = new Map(localCartItems.map((item) => [item.id, item]));
      // Iterate through prevLocalSelectedItems and compare is_unavailable or order weight > total available quantity
      prevLocalSelectedItems.forEach((prevItem) => {
        const latestItem = latestCartItemsMap.get(prevItem.id);
        const blockedCartItems =
          latestItem.is_unavailable ||
          (!latestItem.is_back_order &&
            latestItem.total_available_quantity !== null &&
            (latestItem.is_order_by_weight ? latestItem.order_weight : latestItem.order_qty) >
              latestItem.total_available_quantity);
        if (blockedCartItems) {
          this.itemsUpdate = {
            // set order weight and order quantity same with total available quantity
            ...latestItem,
            order_weight: latestItem.total_available_quantity
              ? Number(latestItem.total_available_quantity.toFixed(2))
              : latestItem.order_weight,
            order_qty: latestItem.total_available_quantity
              ? Number(latestItem.total_available_quantity)
              : latestItem.order_qty
          };
          this.updateQuantityWeightItem(this.itemsUpdate);
        }
      });
    },

    async getAllUserPermissions() {
      const response = await apolloClient.query({
        query: allUserPermissions
      });
      this.permissions = response?.data?.allUserPermissions?.permissions || [];
    },

    checkPermission(code = '') {
      return this.permissions.some((i) => i.code === code);
    },

    async getDeliveryFeeConfigFn() {
      try {
        const { data } = await apolloClient.query({
          query: getDeliveryFeeConfig,
          variables: {
            tenantId: this.user.tenant.id,
            customerId: this.selectedCompany.id
          }
        });

        if (data.getDeliveryFeeConfig) {
          this.deliveryFeeConfig = data.getDeliveryFeeConfig;
        }
      } catch (error) {
        console.error(error);
      }
    },

    async clearCart() {
      // Show confirmation dialog
      await this.createAlertTwoAction(
        this.$t('clear_cart'),
        this.$t('clear_cart_confirmation'),
        this.$t('yes_clear'),
        async () => {
          try {
            // Remove all items from cart
            const itemsToRemove = this.localCart.items.slice(); // Create a copy

            this.showLoading(async () => {
              // Remove each item individually since there's no bulk remove
              const removePromises = itemsToRemove.map((item) =>
                this[ACTIONS_CART.REMOVE_CART_ITEM]({ id: item.id })
              );

              await Promise.allSettled(removePromises);

              // Refresh cart items and clear local state
              this.localCart.items = [];
              this.groupedCartByTenant = [];
              await this.refetchCartItems();
            });

            // Show success message
            this.createAlert(
              this.$t('success'),
              this.$t('cart_cleared_successfully'),
              () => {},
              this.$t('OK')
            );
          } catch (error) {
            console.error('Error clearing cart:', error);
            this.createAlert(this.$t('errorTitle'), this.$t('error_clearing_cart'), () => {}, this.$t('OK'));
          }
        },
        this.$t('cancel'),
        'alert-vertical-action'
      );
    }
  }
});
</script>
<style lang="scss" scoped>
.cart-empty {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 88vh;
  position: fixed;
}

.cart-empty-img {
  height: 128px;
  width: 128px;
}

.text-capitalize {
  text-transform: capitalize;
}

.msg-empty {
  font-size: 12px;
}

.tenant_name {
  margin-top: 3px;
}

.delivery_fee_warn {
  font-size: 12px;
  height: 15px;
}

.block-tenant-list {
  background-color: #eceff1;
}

.block-tenant-item {
  background-color: white;
}

ion-content::part(background) {
  --background: #eceff1;
}
</style>

<style lang="scss">
.block-tenant-list .item-checkbox {
  font-size: 22px;
}

.modal-delete {
  --width: calc(100% - 3rem);
  --height: 30%;
}

.modal-delete .modal-wrapper {
  border-radius: 12px;
}

.modal-update-price {
  --width: 80%;
  --height: 30%;
}
</style>
